import { PrimitiveAtom } from "jotai";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { useMeasure } from "react-use";
import videojs, { VideoJsPlayer } from "video.js";
import "video.js/dist/video-js.css";
import i18n from "../../../../../i18n.ts";
import audioOnlySvg from "../../../../images/audio-only-thumbnail.svg";
import { binSearch } from "../../../../utilities/timestampBinSearch";
import { TimestampsArr, VideoEvent } from "../../../../utilities/useTimestamps";
import { DetectionOverlay } from "../overlay/DetectionOverlay.tsx";
import {
	useMachineLearningAspectRatio,
	useMachineLearningEventsFromDataDump,
	useMachineLearningNearestDetection,
} from "../overlay/hooks.tsx";
import { VideoPlaybackAction, VideoPlaybackState } from "../ProctoringResultView.tsx";
import { VideoSettings } from "../ProctoringVideoPlayer.tsx";
import DeletedIcon from "../Resources/deleted-icon.svg?react";
import UnavailableIcon from "../Resources/UnavailableIcon.tsx";
import { VideoDisplayState } from "../store.ts";
import styles from "./VideoJS.module.css";

export type VideoSegmentUrls = Record<string, string>;

const VideoPlayerUICard = ({ icon, title, text }: { icon: ReactElement; title: string; text: string }) => {
	const [playerCardRef, playerCardSize] = useMeasure<HTMLDivElement>();
	return (
		<div
			className={`${styles.video_thumbnail_wrapper} ${playerCardSize.width < 300 || playerCardSize.height < 300 ? styles.small : ""}`}
			ref={playerCardRef}
		>
			<div className={styles.video_thumbnail_styling}>
				<div className={styles.video_thumbnail_icon_wrapper}>{icon}</div>
				<p className={styles.video_thumbnail_title}>{title}</p>
				<p className={styles.video_thumbnail_text}>{text}</p>
			</div>
		</div>
	);
};

export type VideoJSProps = {
	className?: string;
	events: VideoEvent[];
	debugging_string: string | null | undefined;
	dispatch: React.Dispatch<VideoPlaybackAction>;
	options: VideoSettings | null | undefined;
	controls: {
		isPlaying: boolean;
		playbackSpeed: number;
		volume: number;
	};
	startDate: Date;
	arrStamps: TimestampsArr;
	sources: VideoSegmentUrls;
	displayStateAtom: PrimitiveAtom<VideoDisplayState>;
	videoTimer: VideoPlaybackState;
	toggledTimestamps: boolean;
	isFullscreen: boolean;
	showAudioThumbnail?: boolean;
	setVideoDuration?: (vidDuration: number) => void;
	isDeleted?: boolean;
	deleteCause?: string | null;
	isIncomplete?: boolean;
	videoHasError: boolean;
	setVideoHasError: (value: boolean) => void;
	/**
	 * Called at most once per mounted source when a non-decode load error (eg. a signed URL that 403s
	 * because it was requested before the CDN auth handshake settled) survives every same-URL retry.
	 * Lets the parent ask for a freshly-signed URL instead of leaving the camera latched unavailable.
	 */
	onSourceLoadFailure?: () => void;
	selectedProctoringSessionUuid?: string;
	showMachineLearningOverlay?: boolean;
	/**
	 * Set once the playhead passes the end of a recording that the institution's limit cut short, so
	 * the player explains the blank frame instead of leaving it looking like a malfunction.
	 */
	isPastRecordingLimit?: boolean;
};

/**
 * Backend placeholder for a segment whose recording hasn't finished processing/signing yet (see
 * `shouldShowDownload` in useVideoEvents.ts). Handing this straight to video.js as a `src` fails
 * immediately with MEDIA_ERR_SRC_NOT_SUPPORTED, so treat it the same as "no URL yet".
 */
const UNRESOLVED_SOURCE_PLACEHOLDER = "processing";

export const VideoJS = (props: VideoJSProps) => {
	//#region Variables
	const {
		options,
		controls,
		arrStamps,
		videoTimer,
		startDate,
		dispatch,
		sources,
		isFullscreen,
		showAudioThumbnail,
		setVideoDuration,
		isDeleted,
		deleteCause,
		isIncomplete,
		videoHasError,
		isPastRecordingLimit,
		setVideoHasError,
		selectedProctoringSessionUuid,
		showMachineLearningOverlay = false,
		onSourceLoadFailure,
	} = props;

	const [playerReady, setPlayerReady] = useState(false);
	const [isInitializing, setIsInitializing] = useState(true);

	/* Global time in seconds, derived from videoTimer */
	const globaltime = videoTimer.timer / 1000;

	/* Reference to the HTML video element */
	const videoRef = useRef<HTMLVideoElement | null>(null);

	const videoContainerRef = useRef<HTMLDivElement | null>(null);

	/* Reference to the Video.js player instance */
	const playerRef = useRef<VideoJsPlayer | null>(null);

	const videoTimestampArr = arrStamps[binSearch(arrStamps, globaltime)];
	const isActiveVideoSegment =
		!isDeleted &&
		videoTimestampArr.start !== undefined &&
		globaltime >= videoTimestampArr.start &&
		(videoTimestampArr.end === undefined || globaltime <= videoTimestampArr.end);
	const rawVideoSrc = isActiveVideoSegment ? sources[videoTimestampArr.vid] : undefined;
	const videoSrc = rawVideoSrc && rawVideoSrc !== UNRESOLVED_SOURCE_PLACEHOLDER ? rawVideoSrc : undefined;
	/* Tracks the videoSrc last seen by the load effect, so a re-run triggered purely by our own
	 * setVideoHasError(true) (same source, still broken) doesn't re-attempt the load, while a re-run
	 * carrying a genuinely new/refreshed source still does. */
	const lastAttemptedSrcRef = useRef<string | undefined>(undefined);
	const onSourceLoadFailureRef = useRef(onSourceLoadFailure);
	onSourceLoadFailureRef.current = onSourceLoadFailure;
	const hasRequestedSourceRetryRef = useRef(false);
	const playbackStateRef = useRef({
		controlsIsPlaying: controls.isPlaying,
		globaltime,
		isActiveVideoSegment,
		videoTimerTime: videoTimer.timer,
		videoTimestampArr,
	});
	playbackStateRef.current = {
		controlsIsPlaying: controls.isPlaying,
		globaltime,
		isActiveVideoSegment,
		videoTimerTime: videoTimer.timer,
		videoTimestampArr,
	};

	/* Error handling state */
	const errorState = useRef<{
		recoveryAttempts: number;
		MAX_RECOVERY_ATTEMPTS: number;
		handlerPromise: Promise<void> | null;
		/* Same-URL retry bookkeeping for non-decode errors (eg. a signed URL 403 hit before the CDN
		 * auth handshake settled). Reset whenever the failing src changes. */
		srcRetry: { src: string | null; attempts: number };
		MAX_SRC_RETRY_ATTEMPTS: number;
	}>({
		recoveryAttempts: 0,
		MAX_RECOVERY_ATTEMPTS: 5,
		handlerPromise: null,
		srcRetry: { src: null, attempts: 0 },
		MAX_SRC_RETRY_ATTEMPTS: 3,
	});

	useEffect(() => {
		// A re-run triggered only by our own setVideoHasError(true) latching (no new URL) must not
		// re-attempt the load — see the retry-gating comment below.
		const isNewSrc = lastAttemptedSrcRef.current !== videoSrc;
		lastAttemptedSrcRef.current = videoSrc;

		if (!videoSrc || !videoRef.current) {
			if (playerRef.current) {
				playerRef.current.pause();
				playerRef.current.src("");
				playerRef.current.load();
			}
			setPlayerReady(true);
			setIsInitializing(false);
			return;
		}

		if (isNewSrc) {
			hasRequestedSourceRetryRef.current = false;
			// Decode-recovery attempts are a per-source budget. Without this reset the counter accrues
			// across every segment this camera ever plays, so a handful of benign decode hiccups on an
			// earlier segment could "max out" recovery for a later, unrelated segment and latch it
			// unavailable prematurely.
			errorState.current.recoveryAttempts = 0;
		}

		if (videoSrc && videoRef.current) {
			if (!playerRef.current) {
				const player = videojs(videoRef.current, {
					...options,
					sources: [
						{
							src: videoSrc,
							type: "video/webm",
						},
					],
					playbackRates: [0.25, 0.5, 1, 1.25, 1.5, 1.75, 2, 4],
					preload: "auto",
					autoplay: false,
					normalizeAutoplay: false,
					fill: false,
					controls: false,
				});
				playerRef.current = player;
				setIsInitializing(true);

				player.on("loadedmetadata", () => {
					/* Update parent video duration */
					if (setVideoDuration) {
						let videoDuration = player.duration();
						if (videoDuration === Infinity) {
							// video's metadata is likely unreadable
							videoDuration = 0;
						}
						setVideoDuration(videoDuration * 1000);
					}
				});

				/**
				 * Disable native error display
				 */
				player.errorDisplay.dispose();

				/**
				 * Error handling callback for the video component
				 */
				const errorHandler = async () => {
					try {
						// Get and verify the error exists
						const error = player.error();
						if (!error) {
							return;
						}
						const playbackState = playbackStateRef.current;
						const targetTime =
							playbackState.videoTimestampArr.start !== undefined
								? Math.max(0, playbackState.globaltime - playbackState.videoTimestampArr.start)
								: player.currentTime();
						const playerDuration = player.duration();
						const isAtMediaEnd = Number.isFinite(playerDuration) && targetTime >= Math.max(0, playerDuration - 0.25);
						const isAtSegmentEnd =
							playbackState.videoTimestampArr.end !== undefined &&
							playbackState.globaltime >= playbackState.videoTimestampArr.end - 0.25;

						if (!playbackState.isActiveVideoSegment || isAtMediaEnd || isAtSegmentEnd) {
							player.pause();
							setPlayerReady(true);
							setIsInitializing(false);
							return;
						}

						// If it is not a decode error (eg. MEDIA_ERR_SRC_NOT_SUPPORTED from a signed URL that
						// 403s because it was requested before the CDN auth handshake settled), retry the
						// same URL a few times before giving up — a fresh CDN session or in-flight signature
						// often resolves within a second or two. Only latch the error once retries for this
						// exact source are exhausted, and ask the parent for a freshly-signed URL once.
						if (error.code !== error.MEDIA_ERR_DECODE) {
							const failedSrc = player.currentSrc() || lastAttemptedSrcRef.current || null;
							const srcRetry = errorState.current.srcRetry;
							if (srcRetry.src !== failedSrc) {
								srcRetry.src = failedSrc;
								srcRetry.attempts = 0;
							}

							if (failedSrc && srcRetry.attempts < errorState.current.MAX_SRC_RETRY_ATTEMPTS) {
								srcRetry.attempts += 1;
								const delayMs = 750 * srcRetry.attempts;
								setTimeout(() => {
									// The player instance is reused across segment changes (only .src() swaps), so
									// checking playerRef.current alone doesn't catch "user has since seeked to a
									// different segment" — compare against the source the load effect most recently
									// wanted, and bail rather than stomping it with this stale retry.
									if (playerRef.current !== player || player.isDisposed()) return;
									if (lastAttemptedSrcRef.current !== failedSrc) return;
									player.src([{ src: failedSrc, type: "video/webm" }]);
									player.load();
								}, delayMs);
								return;
							}

							if (!hasRequestedSourceRetryRef.current) {
								hasRequestedSourceRetryRef.current = true;
								onSourceLoadFailureRef.current?.();
							}
							setVideoHasError(true);
							player.hide();
							return;
						} else {
							errorState.current.recoveryAttempts += 1;
						}

						// If it is a decode error, attempt recovery up to the defined max amount of times
						if (errorState.current.recoveryAttempts < errorState.current.MAX_RECOVERY_ATTEMPTS) {
							// Estimate desired video time
							const roundedTargetTime = Math.round(targetTime);

							// Attach a callback to wait for the ready event, then reset the player source and state
							const playerReadyPromise = new Promise<void>((resolve) => {
								player.one("ready", () => {
									resolve();
								});
							});

							const currentSrc = player.currentSrc();
							player.pause();
							player.reset();
							player.src(currentSrc);
							player.load();

							await playerReadyPromise;

							// Calculate step size based on recovery attempt
							// Start with larger steps, then get more conservative with each attempt
							const defaultInitialStepSize = 60;
							const stepSize = Math.max(
								1,
								Math.round(Math.min(defaultInitialStepSize, roundedTargetTime) / errorState.current.recoveryAttempts),
							);

							// Start from the beginning and seek progressively
							let currentPosition = 0;

							// Keep seeking in steps until we reach the target time
							while (currentPosition < roundedTargetTime) {
								// First, seek to the current position
								player.currentTime(currentPosition);

								// Play for a tiny moment to ensure the buffer is loaded
								await player.play();
								player.pause();

								// Calculate next position
								currentPosition = Math.round(currentPosition + stepSize);
							}

							// Restore original playback state
							if (playbackState.controlsIsPlaying) {
								await player.play();
							}

							// Successfully reached near the target position. Resynchronize players using dispatch
							dispatch({ type: "skip", time: playbackState.videoTimerTime });
						} else {
							// If max recovery attempts reached, show error state
							setVideoHasError(true);
							player.hide();
						}
					} catch (error) {
						console.error("Error during video recovery:", error);
					}
				};

				/**
				 * On error, wait for the existing handler to finish, then trigger a new one
				 */
				player.on("error", async () => {
					if (errorState.current.handlerPromise) {
						await errorState.current.handlerPromise;
					}
					errorState.current.handlerPromise = errorHandler();
				});
			} else if (isNewSrc) {
				// Only reassign when the source actually changed — a re-run caused solely by our own
				// setVideoHasError(true) would otherwise reload the same already-failed URL for nothing.
				playerRef.current?.src([{ src: videoSrc, type: "video/webm" }]);
				setIsInitializing(true);
			}

			// Re-attempt whenever we have a source we haven't tried yet, even if a *previous* source on
			// this same camera left videoHasError latched — otherwise a fresh signed URL (or the next
			// segment) never gets re-evaluated and the "unavailable" card sticks forever. A re-run of
			// this effect caused only by our own setVideoHasError(true) (isNewSrc false) is skipped so we
			// don't loop retrying a URL we already gave up on.
			if ((!videoHasError || isNewSrc) && !isDeleted && !isIncomplete) {
				setPlayerReady(false);
				const player = playerRef.current;
				player.one("canplaythrough", () => {
					errorState.current.srcRetry = { src: null, attempts: 0 };
					errorState.current.recoveryAttempts = 0;
					if (videoHasError) {
						// This source loaded fine after all — drop the latch instead of leaving a working
						// camera stuck behind a stale "Recording Is Unavailable" card.
						setVideoHasError(false);
					}
					player.one("timeupdate", () => {
						player.pause();
						if (controls.volume > 0) {
							player.muted(false);
						}
						setPlayerReady(true);
						setIsInitializing(false);
						dispatch({ type: "skip", time: videoTimer.timer });
					});
					player.muted(true);
					void player.play();
				});
			} else {
				playerRef.current?.pause();
				setPlayerReady(true);
				setIsInitializing(false);
			}
		}
	}, [videoSrc, setVideoHasError, videoHasError, isDeleted, isIncomplete]);

	useEffect(() => {
		return () => {
			if (!playerRef.current) return;
			playerRef.current.pause();
			playerRef.current.src("");
			playerRef.current.load();
			playerRef.current.dispose();
			playerRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (!playerReady) return;
		if (!videoTimer.skip) return;
		if (!videoTimestampArr || videoTimestampArr.start === undefined) return;
		if (!isActiveVideoSegment) return;
		if (videoHasError && !isDeleted && !isIncomplete) {
			setVideoHasError(false);
		}
		const player = playerRef.current;
		if (!player) return;
		player.show();
		player.currentTime(Math.max(0, globaltime - videoTimestampArr.start));
		if (controls.isPlaying) {
			void player.play();
		}
		setIsInitializing(false);
		dispatch({ type: "pause" });
	}, [
		videoTimer.skip,
		playerReady,
		controls.isPlaying,
		globaltime,
		videoTimestampArr,
		isActiveVideoSegment,
		videoHasError,
		isDeleted,
		isIncomplete,
		setVideoHasError,
		dispatch,
	]);

	/* Effect to update player volume */
	useEffect(() => {
		if (!playerReady) return;
		playerRef.current?.volume(controls.volume);
	}, [controls.volume, playerReady]);

	/* Effect to update playback speed */
	useEffect(() => {
		if (!playerReady) return;
		playerRef.current?.playbackRate(controls.playbackSpeed);
	}, [controls.playbackSpeed, playerReady]);

	/* Effect to handle play/pause state changes */
	useEffect(() => {
		const player = playerRef.current;
		if (!playerReady || !player) return;
		if (controls.isPlaying) {
			void player.play();
		} else {
			// Skip the forced pause if we're already at the real end — our simulated timer can
			// fire slightly ahead of the video's own decode progress, and pausing mid-settle
			// corrupts state for the next seek+replay.
			const playerDuration = player.duration();
			const isAtRealEnd = Number.isFinite(playerDuration) && player.currentTime() >= playerDuration - 0.25;
			if (!isAtRealEnd) {
				player.pause();
			}
		}
	}, [controls.isPlaying, playerReady]);

	useEffect(() => {
		const videoPlayers = videoContainerRef.current?.querySelectorAll('div[data-vjs-player="true"]');
		videoPlayers?.forEach((element) => {
			if (!(element instanceof HTMLElement)) return;
			if (isDeleted) {
				element.style.display = "none";
			} else {
				element.style.display = "";
			}
		});
	}, [isDeleted]);

	const shouldHideDefaultVideoPlayerUI = showAudioThumbnail || videoHasError || isInitializing || !videoSrc;

	useEffect(() => {
		const player = playerRef.current;
		if (!player) return;

		if (shouldHideDefaultVideoPlayerUI) {
			player.hide();
		} else {
			player.show();
		}
	}, [shouldHideDefaultVideoPlayerUI]);

	const { faceDetections, objectDetections } = useMachineLearningEventsFromDataDump(
		selectedProctoringSessionUuid,
		startDate,
	);
	const { nearestFaceDetection, nearestObjectDetection } = useMachineLearningNearestDetection(
		{ faceDetections, objectDetections },
		globaltime,
	);
	const { aspectRatio } = useMachineLearningAspectRatio(faceDetections, objectDetections);

	const playerDivRef = useRef<HTMLDivElement | null>(null);
	const videoPosition = (() => {
		if (playerDivRef.current) {
			const videoRatio = playerDivRef.current.offsetWidth / playerDivRef.current.offsetHeight;
			if (Number.isNaN(videoRatio)) return;

			// If the video is wider than the aspect ratio, add padding to the left and right
			if (videoRatio > aspectRatio) {
				const excessWidth = playerDivRef.current.offsetWidth - playerDivRef.current.offsetHeight * aspectRatio;
				return {
					top: 0,
					left: excessWidth / 2,
					width: playerDivRef.current.offsetWidth - excessWidth,
					height: playerDivRef.current.offsetHeight,
				};
				// vice versa
			} else {
				const excessHeight = playerDivRef.current.offsetHeight - playerDivRef.current.offsetWidth / aspectRatio;
				return {
					top: excessHeight / 2,
					left: 0,
					width: playerDivRef.current.offsetWidth,
					height: playerDivRef.current.offsetHeight - excessHeight,
				};
			}
		}
	})();

	// One flag drives both the explanation and the hiding of the video surface beneath it, so the two
	// can never disagree and leave a stale frame on show.
	const showRecordingLimitCard = !videoHasError && !showAudioThumbnail && !!isPastRecordingLimit;

	return (
		<div
			className={`${styles.videoWrapper} ${showRecordingLimitCard ? styles.past_recording_limit : ""}`}
			ref={videoContainerRef}
		>
			{/* {statusImageProps && <StatusImage {...statusImageProps} />} */}
			{showAudioThumbnail && !videoHasError && (
				<div
					style={{
						height: "100%",
						width: "100%",
						justifyContent: "center",
						display: "flex",
						overflow: "hidden",
					}}
				>
					<img style={{ maxWidth: "100%", width: "inherit" }} src={audioOnlySvg} alt="Audio Only" />
				</div>
			)}

			{videoHasError && (
				<VideoPlayerUICard
					icon={isDeleted ? <DeletedIcon /> : <UnavailableIcon />}
					title={
						isDeleted
							? deleteCause === "expired"
								? i18n.t("result.components.resultsPage.videoPlayerCard.recordingExpiredTitle")
								: i18n.t("result.components.resultsPage.videoPlayerCard.recordingDeletedTitle")
							: isIncomplete
								? i18n.t("result.components.resultsPage.videoPlayerCard.recordingIncompleteTitle")
								: i18n.t("result.components.resultsPage.videoPlayerCard.recordingErrorTitle")
					}
					text={
						isDeleted
							? deleteCause === "expired"
								? i18n.t("result.components.resultsPage.videoPlayerCard.recordingExpiredText")
								: i18n.t("result.components.resultsPage.videoPlayerCard.recordingDeletedText")
							: isIncomplete
								? i18n.t("result.components.resultsPage.videoPlayerCard.recordingIncompleteText")
								: i18n.t("result.components.resultsPage.videoPlayerCard.recordingErrorText")
					}
				/>
			)}

			{showRecordingLimitCard && (
				<div className={styles.recording_limit_overlay}>
					<VideoPlayerUICard
						icon={<UnavailableIcon />}
						title={i18n.t("result.components.resultsPage.videoPlayerCard.recordingLimitReachedTitle")}
						text={i18n.t("result.components.resultsPage.videoPlayerCard.recordingLimitReachedText")}
					/>
				</div>
			)}

			<div data-vjs-player className="video-js" ref={playerDivRef}>
				<video ref={videoRef} onDoubleClick={(e) => e.preventDefault()} />

				{videoPosition && showMachineLearningOverlay && (
					<div
						style={{
							...videoPosition,
							position: "absolute",
							pointerEvents: "none",
						}}
					>
						{(nearestFaceDetection || nearestObjectDetection) && (
							<DetectionOverlay
								faceDetection={nearestFaceDetection}
								objectDetection={nearestObjectDetection}
								styling={{
									isFullscreen,
								}}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default VideoJS;
